<template>
    <div class="delivery-order-page container pb-4">
        <div class="delivery-order-page__toolbar d-flex align-items-center justify-content-between gap-2 mb-3">
            <router-link to="/delivery" class="delivery-order-page__back btn btn-outline-secondary d-inline-flex align-items-center gap-1">
                <BootstrapIcon name="chevron-left" :size="17" />
                <span>{{$t('delivery.all_orders')}}</span>
            </router-link>
            <div v-if="hasOrder" class="delivery-order-page__print-actions d-flex gap-2">
                <button type="button" class="btn btn-outline-teal-600 d-inline-flex align-items-center justify-content-center gap-2" @click="print_a4(safeOrder)">
                    <BootstrapIcon name="printer" :size="17" />
                    <span>{{$t('requisition.print_a4')}}</span>
                </button>
                <button type="button" class="btn btn-outline-teal-600 d-inline-flex align-items-center justify-content-center gap-2" @click="print_a8(safeOrder)">
                    <BootstrapIcon name="receipt" :size="17" />
                    <span>{{$t('requisition.print_receipt')}}</span>
                </button>
            </div>
        </div>

        <div v-if="loading" class="delivery-order-page__loading text-center" role="status">
            <span class="spinner-border text-teal-600" aria-hidden="true"></span>
            <p class="text-gray-600 mt-3 mb-0">{{$t('delivery.loading_order')}}</p>
        </div>

        <template v-else-if="hasOrder">
            <section class="delivery-order-hero mb-3" :class="status.cardClass">
                <div class="delivery-order-hero__main min-w-0">
                    <h1 class="delivery-order-hero__title fw-bolder mb-2">
                        {{$t('delivery.order_reference', { REFERENCE: orderReference })}}
                    </h1>
                    <div class="d-flex align-items-center flex-wrap gap-2">
                        <span class="delivery-order-hero__status" :class="status.pillClass">
                            <BootstrapIcon :color="status.iconColor" name="circle-fill" :size="9" />
                            <span>{{$t(status.labelKey)}}</span>
                        </span>
                        <span v-if="statusNote" class="delivery-order-hero__note">{{statusNote}}</span>
                    </div>
                </div>
                <div v-if="deliveryMemberName" class="delivery-order-hero__member">
                    <span class="delivery-order-hero__member-icon">
                        <BootstrapIcon :name="status.memberIcon" :size="18" />
                    </span>
                    <span class="min-w-0">
                        <small class="d-block">{{$t('delivery.processed_by')}}</small>
                        <strong class="d-block text-truncate">{{deliveryMemberName}}</strong>
                    </span>
                </div>
            </section>

            <div v-if="discardReason" class="delivery-order-page__discard d-flex gap-2 mb-3" role="note">
                <BootstrapIcon class="flex-shrink-0 mt-1" name="exclamation-triangle" :size="18" />
                <p class="mb-0"><strong class="me-1">{{$t('delivery.discard_reason')}}:</strong>{{discardReason}}</p>
            </div>

            <div class="delivery-order-meta mb-3">
                <div
                v-for="detail in orderMetadata"
                :key="detail.key"
                class="delivery-order-meta__item"
                :class="{'delivery-order-meta__item--location': detail.key === 'location'}"
                >
                    <span class="delivery-order-meta__icon">
                        <BootstrapIcon :name="detail.icon" :size="17" />
                    </span>
                    <span class="min-w-0">
                        <small class="delivery-order-meta__label d-block">{{detail.label}}</small>
                        <strong class="delivery-order-meta__value d-block">{{detail.value}}</strong>
                    </span>
                </div>
            </div>

            <div class="row g-3 align-items-start">
                <div :class="hasSidebar ? 'col-12 col-lg-8' : 'col-12'">
                    <section class="delivery-order-panel">
                        <header class="delivery-order-panel__header">
                            <span class="delivery-order-panel__header-icon">
                                <BootstrapIcon name="stack" :size="19" />
                            </span>
                            <h2 class="h6 fw-bolder mb-0">{{productCountText}}</h2>
                        </header>
                        <div class="delivery-order-panel__body delivery-order-panel__body--products">
                            <DeliveryItem
                            v-for="(item, index) in safeItems"
                            :key="itemKey(item, index)"
                            :order_status="safeOrder.status"
                            :_item="item"
                            />
                            <p v-if="safeItems.length === 0" class="text-gray-500 mb-0">{{$t('delivery.no_products')}}</p>
                        </div>
                    </section>
                </div>

                <aside v-if="hasSidebar" class="col-12 col-lg-4">
                    <section v-if="orderData.length > 0" class="delivery-order-panel mb-3">
                        <header class="delivery-order-panel__header">
                            <span class="delivery-order-panel__header-icon">
                                <BootstrapIcon name="file-text" :size="19" />
                            </span>
                            <h2 class="h6 fw-bolder mb-0">{{$t('delivery.order_details')}}</h2>
                        </header>
                        <dl class="delivery-order-panel__list mb-0">
                            <div v-for="detail in orderData" :key="detail.key" class="delivery-order-panel__list-item">
                                <dt>{{detail.label}}</dt>
                                <dd>{{detail.value}}</dd>
                            </div>
                        </dl>
                    </section>

                    <section v-if="customerDetails.length > 0" class="delivery-order-panel">
                        <header class="delivery-order-panel__header">
                            <span class="delivery-order-panel__header-icon">
                                <BootstrapIcon name="person" :size="19" />
                            </span>
                            <h2 class="h6 fw-bolder mb-0">{{$t('delivery.customer')}}</h2>
                        </header>
                        <dl class="delivery-order-panel__list mb-0">
                            <div v-for="detail in customerDetails" :key="detail.key" class="delivery-order-panel__list-item">
                                <dt>{{detail.label}}</dt>
                                <dd>{{detail.value}}</dd>
                            </div>
                        </dl>
                    </section>
                </aside>
            </div>

            <div v-if="hasActions" class="delivery-order-actions mt-3">
                <button v-if="canDiscard" type="button" class="btn btn-outline-orange-400" :disabled="loading" @click="request_confirm_discard_order()">
                    <BootstrapIcon name="file-earmark-minus" :size="19" />
                    <span>{{$t('delivery.discard')}}</span>
                </button>
                <button v-if="canMoveToPending" type="button" class="btn btn-outline-secondary" :disabled="loading" @click="update_delivery(STATUS_PENDING)">
                    <BootstrapIcon name="arrow-counterclockwise" :size="19" />
                    <span>{{$t('delivery.pending')}}</span>
                </button>
                <button v-if="canPrepare" type="button" class="btn btn-success" :disabled="loading" @click="update_delivery(STATUS_PREPARING)">
                    <BootstrapIcon color="fill-white text-white" name="play-circle" :size="19" />
                    <span>{{$t('delivery.prepare')}}</span>
                </button>
                <button v-if="canComplete" type="button" class="btn btn-info" :disabled="loading" @click="update_delivery(STATUS_COMPLETED)">
                    <BootstrapIcon name="check-circle" :size="19" />
                    <span>{{$t('delivery.complete')}}</span>
                </button>
                <button v-if="canHandToCustomer" type="button" class="btn btn-brown-500 text-white" :disabled="loading" @click="update_delivery(STATUS_DELIVERED)">
                    <BootstrapIcon color="fill-white text-white" name="box-arrow-right" :size="19" />
                    <span>{{$t('delivery.handed_to_customer')}}</span>
                </button>
            </div>
        </template>

        <div v-else class="delivery-order-page__empty">
            <EmptyResponse :description="$t('delivery.no_orders')" />
        </div>

        <ModalConfirmDiscardOrder :order="hasOrder ? safeOrder : null" />
    </div>
</template>

<script>
import BootstrapIcon from '@/common/directive/BootstrapIcon.vue'
import InventoryOrderStore from '@/common/store/inventory_order.js'
import EmptyResponse from '@/common/directive/EmptyResponse.vue'
import DeliveryItem from '@/common/directive/DeliveryItem.vue'
import { REGEX_UUID } from '@/common/constants.js'
import DateFormat from 'date-fns/format'
import { enUS, ro } from 'date-fns/locale'
import { createApp, defineComponent } from 'vue'
import TemplatePrintA4 from '@/modules/store/template/print_a4.vue'
import TemplatePrintA8 from '@/modules/store/template/print_a8.vue'
import ModalConfirmDiscardOrder from '@/common/modal/modal-confirm_discard_order.vue'

const STATUS = Object.freeze({
    order_pending: {
        labelKey: 'delivery.pending',
        cardClass: 'delivery-order-hero--pending',
        pillClass: 'delivery-order-hero__status--pending',
        iconColor: 'fill-coral-500 text-coral-500',
        memberIcon: 'cart'
    },
    order_preparing: {
        labelKey: 'delivery.preparing',
        cardClass: 'delivery-order-hero--preparing',
        pillClass: 'delivery-order-hero__status--preparing',
        iconColor: 'fill-teal-500 text-teal-500',
        memberIcon: 'cart'
    },
    order_completed: {
        labelKey: 'delivery.completed',
        cardClass: 'delivery-order-hero--completed',
        pillClass: 'delivery-order-hero__status--completed',
        iconColor: 'fill-cyan-500 text-cyan-500',
        memberIcon: 'cart-check'
    },
    order_discarded: {
        labelKey: 'delivery.discarded',
        cardClass: 'delivery-order-hero--discarded',
        pillClass: 'delivery-order-hero__status--discarded',
        iconColor: 'fill-orange-300 text-orange-300',
        memberIcon: 'cart-x'
    },
    order_delivered: {
        labelKey: 'delivery.handed',
        cardClass: 'delivery-order-hero--delivered',
        pillClass: 'delivery-order-hero__status--delivered',
        iconColor: 'fill-brown-500 text-brown-500',
        memberIcon: 'box-seam'
    }
})

const UNKNOWN_STATUS = Object.freeze({
    labelKey: 'order_widget.unknown_status',
    cardClass: 'delivery-order-hero--unknown',
    pillClass: 'delivery-order-hero__status--unknown',
    iconColor: 'fill-gray-500 text-gray-500',
    memberIcon: 'person'
})

export default {
    components: {
        BootstrapIcon,
        EmptyResponse,
        DeliveryItem,
        ModalConfirmDiscardOrder
    },
    data: function () {
        return {
            STATUS_PENDING: 'order_pending',
            STATUS_PREPARING: 'order_preparing',
            STATUS_COMPLETED: 'order_completed',
            STATUS_DISCARDED: 'order_discarded',
            STATUS_DELIVERED: 'order_delivered',
            order_id: null,
            loading: true,
            order: null,
            business_details: null
        }
    },
    computed: {
        locale: function () {
            return this.$i18n && typeof this.$i18n.locale === 'string' ? this.$i18n.locale : 'en'
        },
        safeOrder: function () {
            return this.order && typeof this.order === 'object' && !Array.isArray(this.order) ? this.order : {}
        },
        hasOrder: function () {
            return this.hasText(this.safeOrder.id)
        },
        safeItems: function () {
            return Array.isArray(this.safeOrder.items)
                ? this.safeOrder.items.filter(item => item && typeof item === 'object' && !Array.isArray(item))
                : []
        },
        safeOrderData: function () {
            return this.safeOrder.data && typeof this.safeOrder.data === 'object' && !Array.isArray(this.safeOrder.data)
                ? this.safeOrder.data
                : {}
        },
        safeCustomer: function () {
            return this.safeOrder.customer && typeof this.safeOrder.customer === 'object' && !Array.isArray(this.safeOrder.customer)
                ? this.safeOrder.customer
                : {}
        },
        status: function () {
            return this.hasText(this.safeOrder.status) && STATUS[this.safeOrder.status]
                ? STATUS[this.safeOrder.status]
                : UNKNOWN_STATUS
        },
        orderReference: function () {
            return this.hasText(this.safeOrder.id) ? this.safeOrder.id.split('-')[0].toUpperCase() : '—'
        },
        deliveryMemberName: function () {
            const member = this.safeOrder.delivery
            return member && typeof member === 'object' && !Array.isArray(member) && this.hasText(member.username)
                ? member.username.trim()
                : ''
        },
        statusNote: function () {
            if (this.safeOrder.status !== this.STATUS_DELIVERED) return ''
            const date = this.format_date(this.safeOrder.updated_at, 'dd MMM yyyy')
            return date ? this.$t('delivery.handed_on', { DATE: date }) : ''
        },
        discardReason: function () {
            return this.safeOrder.status === this.STATUS_DISCARDED && this.hasDisplayValue(this.safeOrder.reason)
                ? this.displayValue(this.safeOrder.reason)
                : ''
        },
        productCountText: function () {
            const key = this.safeItems.length === 1 ? 'order_widget.product_count_one' : 'order_widget.product_count_many'
            return this.$t(key, { COUNT: this.safeItems.length })
        },
        orderMetadata: function () {
            const details = []
            const location = this.safeOrder.location
            const requester = this.safeOrder.requisition
            const locationName = location && typeof location === 'object' && this.hasText(location.name) ? location.name.trim() : ''
            const requesterName = requester && typeof requester === 'object' && this.hasText(requester.username) ? requester.username.trim() : ''
            const created = this.format_date(this.safeOrder.created_at, 'dd MMM yyyy, HH:mm')

            if (locationName) details.push({ key: 'location', icon: 'geo-alt-fill', label: this.$t('delivery.workplace'), value: locationName })
            if (requesterName) details.push({ key: 'requester', icon: 'cart-plus', label: this.$t('delivery.placed_by'), value: requesterName })
            if (created) details.push({ key: 'created', icon: 'clock', label: this.$t('delivery.added_on'), value: created })
            return details
        },
        orderData: function () {
            return Object.entries(this.safeOrderData)
                .filter(([, value]) => this.hasDisplayValue(value))
                .map(([key, value]) => ({
                    key,
                    label: this.translationOrKey(`requisition.${key}`, key),
                    value: this.displayValue(value)
                }))
        },
        customerDetails: function () {
            return [
                { key: 'name', label: this.$t('delivery.name'), value: this.safeCustomer.name },
                { key: 'phone', label: this.$t('delivery.phone'), value: this.safeCustomer.phone },
                { key: 'email', label: this.$t('delivery.email'), value: this.safeCustomer.email },
                { key: 'address', label: this.$t('delivery.address'), value: this.safeCustomer.address }
            ]
                .filter(detail => this.hasDisplayValue(detail.value))
                .map(detail => ({ ...detail, value: this.displayValue(detail.value) }))
        },
        hasSidebar: function () {
            return this.orderData.length > 0 || this.customerDetails.length > 0
        },
        canDiscard: function () {
            return this.safeOrder.status === this.STATUS_PENDING
        },
        canMoveToPending: function () {
            return this.safeOrder.status === this.STATUS_PREPARING
        },
        canPrepare: function () {
            return this.safeOrder.status === this.STATUS_PENDING
        },
        canComplete: function () {
            return this.safeOrder.status === this.STATUS_PREPARING
        },
        canHandToCustomer: function () {
            return this.safeOrder.status === this.STATUS_COMPLETED && this.hasText(this.safeOrder.customer_id) && Object.keys(this.safeCustomer).length > 0
        },
        hasActions: function () {
            return this.canDiscard || this.canMoveToPending || this.canPrepare || this.canComplete || this.canHandToCustomer
        }
    },
    created: async function () {
        const self = this
        if (!this.$route.query.id || !REGEX_UUID.test(this.$route.query.id)) {
            return this.$router.replace('/delivery')
        }
        this.order_id = this.$route.query.id

        this.$bus.$off('Order/status_discarded')
        this.$bus.$on('Order/status_discarded', function () {
            self.get_order()
        })

        await this.get_order()
    },
    methods: {
        hasText: function (value) {
            return typeof value === 'string' && value.trim().length > 0
        },
        hasDisplayValue: function (value) {
            if (this.hasText(value)) return true
            if (typeof value === 'number' || typeof value === 'boolean') return true
            return Array.isArray(value) ? value.length > 0 : Boolean(value && typeof value === 'object' && Object.keys(value).length > 0)
        },
        displayValue: function (value) {
            if (Array.isArray(value)) return value.map(this.displayValue).join(', ')
            if (value && typeof value === 'object') {
                try {
                    return JSON.stringify(value)
                } catch (error) {
                    return ''
                }
            }
            return value === null || typeof value === 'undefined' ? '' : String(value)
        },
        translationOrKey: function (translationKey, fallbackKey) {
            if (typeof this.$te === 'function' && this.$te(translationKey)) return this.$t(translationKey)
            return String(fallbackKey).replace(/_/g, ' ')
        },
        format_date: function (timestamp, format) {
            if (!timestamp) return ''
            const date = new Date(timestamp)
            if (Number.isNaN(date.getTime())) return ''
            return DateFormat(date, format, { locale: this.locale === 'ro' ? ro : enUS })
        },
        itemKey: function (item, index) {
            return this.hasText(item.id) ? item.id : index
        },
        get_order: async function () {
            const response = await InventoryOrderStore.dispatch('GET_ORDER', this.order_id)
            if (response && response.data && response.data.status === 0) {
                this.order = response.data.order
                this.business_details = response.data.business_details
                this.loading = false
            } else {
                this.loading = false
                this.$bus.$emit('Notification/show', { title: this.$t('notifications.something_went_wrong'), msg: this.$t('notifications.please_retry_the_operation_or_refresh'), level: 'danger' })
            }
        },
        update_delivery: async function (status) {
            this.loading = true
            const q = {
                id: this.order_id,
                params: { status }
            }
            const response = await InventoryOrderStore.dispatch('UPDATE_DELIVERY', q)
            if (response && response.status === 0) {
                this.$bus.$emit('Notification/show', { title: this.$t('notifications.operation_success'), msg: this.$t('notifications.changes_saved'), level: 'success' })
            } else {
                this.$bus.$emit('Notification/show', { title: this.$t('notifications.something_went_wrong'), msg: this.$t('notifications.please_retry_the_operation_or_refresh'), level: 'danger' })
            }
            await this.get_order()
            this.loading = false
        },
        print_a8: async function (order) {
            const self = this
            const component = {
                extends: TemplatePrintA8,
                data: function () {
                    return {
                        type: 'delivery',
                        order,
                        created_at: self.format_date(order.created_at, 'dd MMM yyyy HH:mm'),
                        business_details: self.business_details && typeof self.business_details === 'object' ? { ...self.business_details } : {}
                    }
                },
                methods: { $t: self.$t }
            }
            const printWindow = window.open('', '', 'height=500, width=500')
            if (!printWindow) return
            const container = printWindow.document.createElement('div')
            const app = createApp(defineComponent(component)).mount(container)

            printWindow.document.write(`<html><body>${app.$el.outerHTML}</body></html>`)
            printWindow.document.close()
            printWindow.print()
        },
        print_a4: async function (order) {
            const self = this
            const component = {
                extends: TemplatePrintA4,
                data: function () {
                    return {
                        order,
                        created_at: self.format_date(order.created_at, 'dd MMM yyyy HH:mm'),
                        business_details: self.business_details && typeof self.business_details === 'object' ? { ...self.business_details } : {}
                    }
                },
                methods: { $t: self.$t }
            }
            const printWindow = window.open('', '', 'height=500, width=500')
            if (!printWindow) return
            const container = printWindow.document.createElement('div')
            const app = createApp(defineComponent(component)).mount(container)

            printWindow.document.write(`<html><body>${app.$el.outerHTML}</body></html>`)
            printWindow.document.close()
            printWindow.print()
        },
        request_confirm_discard_order: function () {
            this.$bus.$emit('Modal/show/confirm-order-discard')
        }
    }
}
</script>
